import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth, WorkspaceMembership, VERSION_INFO } from '../contexts/AuthContext';
import GlobalSearch from './GlobalSearch';
import NotificationCenter from './alerts/NotificationCenter';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { apiGet } from '../utils/api';
import {
  HomeIcon,
  UsersIcon,
  MapIcon,
  AcademicCapIcon,
  CogIcon,
  Bars3Icon,
  ChevronDownIcon,
  ShoppingCartIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  HeartIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showWorkspaceSwitcher, setShowWorkspaceSwitcher] = useState(false);
  const [memberships, setMemberships] = useState<WorkspaceMembership[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  // Confirmed marketplace membership drives the Marketplace nav entry — never
  // the user's agricultural role. Value is the portal entry path.
  const [marketplaceEntry, setMarketplaceEntry] = useState<string | null>(null);
  const [switchingTenantId, setSwitchingTenantId] = useState<number | null>(null);
  const [workspaceError, setWorkspaceError] = useState('');
  const location = useLocation();
  const {
    user,
    logout,
    getMemberships,
    switchWorkspace,
    hasFeature,
    canAccessPage,
    accessReady,
    hasAnyOperationalModule,
  } = useAuth();
  const { t } = useTranslation();
  const isSuperAdmin = user?.role === 'super_admin';
  const tenantName = user?.tenant?.name || user?.tenant?.company_name || user?.tenant?.legal_name || (isSuperAdmin ? 'Platform Owner' : 'Workspace');
  const tenantDomain = user?.tenant?.domain || user?.tenant?.admin_domain || '';
  const tenantCountry = user?.tenant?.country || '';
  const tenantCurrency = user?.tenant?.default_currency || '';
  const effectiveRoles = user?.role
    ? [user.role]
    : [];

  // Helper for icons not imported above (to keep imports clean)
  function UserGroupIcon(props: any) { return <UsersIcon {...props} /> } // Reusing UsersIcon for now or import real one
  function RecycleIcon(props: any) { return <CogIcon {...props} /> }
  function BuildingStorefrontIcon(props: any) { return <MapIcon {...props} /> }
  function GlobeAltIcon(props: any) { return <MapIcon {...props} /> }
  function SproutIcon(props: any) { return <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg> }
  function WrenchIcon(props: any) { return <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.42 15.17l-7.49 7.49a2 2 0 11-2.83-2.83l7.49-7.49m4.24-4.24l-1.41-1.41a4 4 0 00-5.66 0L4.93 10.1a8 8 0 001.41 1.41l1.41 1.41m7.07-7.07l1.41 1.41a4 4 0 010 5.66l-3.54 3.54a8 8 0 01-1.41-1.41l-1.41-1.41m5.66-5.66A4 4 0 0118.34 5.66l1.41 1.41a4 4 0 010 5.66l-3.54 3.54" /></svg> }

  const navigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon, roles: ['all'], page: 'dashboard', feature: 'dashboard', group: '_top' },
    { name: t('nav.farmers'), href: '/dashboard/farmers', icon: UsersIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'farmers', feature: 'farmers', group: 'Farm Operations' },
    { name: t('nav.cohorts'), href: '/dashboard/cohorts', icon: MapIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'cohorts', feature: 'cohorts', group: 'Farm Operations' },
    { name: t('nav.vsla'), href: '/dashboard/vsla', icon: UserGroupIcon, roles: ['project_manager', 'field_facilitator', 'super_admin'], page: 'vsla', feature: 'vsla', group: 'Farm Operations' },
    { name: 'Crops', href: '/dashboard/crops', icon: SproutIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'crops', feature: 'crops', group: 'Farm Operations' },
    { name: 'Activities', href: '/dashboard/activities', icon: ClipboardDocumentListIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'activities', feature: 'activities', group: 'Farm Operations' },
    { name: 'Morning Rounds', href: '/dashboard/morning-rounds', icon: ClipboardDocumentListIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'activities', feature: 'activities', group: 'Farm Operations' },
    { name: 'Field Intelligence', href: '/dashboard/field-intelligence', icon: ChartBarIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'activities', feature: 'activities', group: 'Farm Operations' },
    { name: 'Field Diary', href: '/dashboard/field-diary', icon: CalendarDaysIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'field-diary', feature: 'field_diary', group: 'Farm Operations' },
    { name: 'Tree Inspections', href: '/dashboard/tree-inspections', icon: ClipboardDocumentCheckIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'tree-inspections', feature: 'traceability', group: 'Farm Operations' },
    { name: 'Traceability', href: '/dashboard/traceability', icon: GlobeAltIcon, roles: ['project_manager', 'agronomist', 'field_facilitator', 'super_admin'], page: 'traceability', feature: 'traceability', group: 'Supply Chain' },
    { name: 'Warehouse', href: '/dashboard/warehouse', icon: BuildingStorefrontIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'warehouse', feature: 'warehouse', group: 'Supply Chain' },
    { name: t('nav.compost'), href: '/dashboard/compost', icon: RecycleIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'compost', feature: 'compost', group: 'Supply Chain' },
    // Marketplace portal — only shown when the user has confirmed marketplace
    // membership (see marketplaceEntry below). Supplier access is never granted
    // based on the agricultural role alone.
    ...(marketplaceEntry ? [{ name: 'Marketplace', href: marketplaceEntry, icon: BuildingStorefrontIcon, roles: ['all'], feature: 'marketplace', group: 'Marketplace' }] : []),
    // Legacy tenant operations Orders page — only shown when the tenant's
    // marketplace is actually enabled (or for the platform owner). Without
    // marketplace entitlement the orders workflow does not exist.
    ...(marketplaceEntry || isSuperAdmin ? [{ name: t('nav.orders'), href: '/dashboard/orders', icon: ShoppingCartIcon, roles: ['all'], page: 'orders', feature: 'orders', group: 'Operations' }] : []),
    { name: t('nav.training'), href: '/dashboard/training', icon: AcademicCapIcon, roles: ['project_manager', 'field_facilitator', 'agronomist', 'super_admin'], page: 'training', feature: 'training', group: 'Learning' },
    { name: t('nav.guidance'), href: '/dashboard/guidance', icon: AcademicCapIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'guidance', feature: 'advisory', group: 'Learning' },
    { name: 'Money', href: '/dashboard/money', icon: BanknotesIcon, roles: ['owner', 'project_manager', 'agronomist', 'field_facilitator', 'tenant_admin', 'business_manager', 'finance_admin'], page: 'farm-finance', feature: 'farm_finance', group: 'Finance' },
    { name: 'Analytics', href: '/dashboard/analytics', icon: ChartBarIcon, roles: ['owner', 'project_manager', 'super_admin'], page: 'analytics', group: 'Finance' },
    { name: 'Issues', href: '/dashboard/issues', icon: ExclamationCircleIcon, roles: ['owner', 'project_manager', 'field_facilitator', 'agronomist'], page: 'issues', feature: 'issues', group: 'Support' },
    { name: t('nav.users'), href: '/dashboard/users', icon: UserCircleIcon, roles: ['project_manager', 'super_admin'], page: 'users', group: '_admin' },
    { name: 'Donations', href: '/dashboard/donations', icon: HeartIcon, roles: ['super_admin', 'project_manager'], page: 'donations', feature: 'donations', group: 'Governance' },
    { name: 'Funding Readiness', href: '/dashboard/funding-readiness', icon: ChartBarIcon, roles: ['super_admin', 'project_manager'], page: 'funding-readiness', feature: 'funding_readiness', group: 'Governance' },
    { name: 'Audit Logs', href: '/dashboard/audit-logs', icon: ClipboardDocumentListIcon, roles: ['super_admin'], page: 'audit-logs', group: 'Governance' },
    { name: 'Platform Overview', href: '/dashboard/super-admin', icon: ShieldCheckIcon, roles: ['super_admin'], group: '_superadmin' },
    { name: 'Manage Organisations', href: '/dashboard/company-management', icon: BuildingOffice2Icon, roles: ['super_admin'], group: '_superadmin' },
    { name: 'Manage Users', href: '/dashboard/users', icon: UserCircleIcon, roles: ['super_admin'], group: '_superadmin' },
    { name: 'Manage Subscriptions', href: '/dashboard/subscriptions', icon: ChartBarIcon, roles: ['super_admin'], group: '_superadmin' },
    { name: 'Web Contact Form Inbox', href: '/dashboard/website-control?tab=inquiries', icon: EnvelopeIcon, roles: ['super_admin'], group: '_superadmin' },
    { name: 'Blog Posts', href: '/dashboard/blogs', icon: DocumentTextIcon, roles: ['super_admin'], page: 'website-control', feature: 'website_control', group: '_superadmin' },
    { name: 'Settings', href: '/dashboard/settings', icon: WrenchIcon, roles: ['super_admin'], group: '_superadmin' },
    { name: t('nav.maps'), href: '/dashboard/maps', icon: GlobeAltIcon, roles: ['project_manager', 'agronomist', 'super_admin'], page: 'maps', feature: 'maps', group: 'Supply Chain' },
  ];

  // Effective access: a missing/unknown key is denied. Super Admin is the only
  // role that bypasses tenant entitlements.
  const isTenantEntryEnabled = (item: any) => {
    if (isSuperAdmin) return true;
    return canAccessPage(item.page) && hasFeature(item.feature);
  };

  const filteredNavigation = navigation.filter(item =>
    (item.roles.includes('all') || effectiveRoles.some((role) => item.roles.includes(role))) && isTenantEntryEnabled(item)
  );
  const superAdminPrimary = filteredNavigation.filter(i => i.group === '_superadmin');
  const normalPrimary = filteredNavigation.filter(i => i.group !== '_superadmin' && i.group !== 'Governance');
  const noModulesEnabled = !isSuperAdmin && accessReady && !hasAnyOperationalModule;
  const normalGovernance = filteredNavigation.filter(i => i.group === 'Governance');
  const hasMultipleWorkspaces = memberships.length > 1;

  // Resolve marketplace portal entry from confirmed membership/capabilities.
  useEffect(() => {
    if (isSuperAdmin || !user) return;
    let cancelled = false;
    const loadMarketplaceEntry = async () => {
      try {
        const data = await apiGet<any>('/api/marketplace/b2b/membership-status', undefined, { silent: true });
        if (cancelled) return;
        if (data.hasMembership) {
          const orgType = data.organisationType;
          if (orgType === 'buyer') setMarketplaceEntry('/marketplace/buyer');
          else if (orgType === 'both') setMarketplaceEntry('/marketplace/buyer');
          else setMarketplaceEntry('/marketplace/supplier');
        } else {
          setMarketplaceEntry(null);
        }
      } catch {
        if (!cancelled) setMarketplaceEntry(null);
      }
    };
    loadMarketplaceEntry();
    return () => { cancelled = true; };
  }, [user, isSuperAdmin]);

  useEffect(() => {
    if (!user || isSuperAdmin) {
      setMemberships([]);
      return;
    }

    let cancelled = false;
    const loadMemberships = async () => {
      try {
        setMembershipsLoading(true);
        setWorkspaceError('');
        const items = await getMemberships();
        if (!cancelled) setMemberships(items);
      } catch (error: any) {
        if (!cancelled) setWorkspaceError(error.message || 'Could not load workspaces');
      } finally {
        if (!cancelled) setMembershipsLoading(false);
      }
    };

    loadMemberships();
    return () => {
      cancelled = true;
    };
  }, [user, isSuperAdmin, getMemberships]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleSwitchWorkspace = async (tenantId: number) => {
    if (!tenantId || tenantId === user?.tenant_id || switchingTenantId) return;
    try {
      setSwitchingTenantId(tenantId);
      setWorkspaceError('');
      await switchWorkspace(tenantId);
      setShowUserMenu(false);
      setShowWorkspaceSwitcher(false);
      setSidebarOpen(false);
    } catch (error: any) {
      setWorkspaceError(error.message || 'Could not switch workspace');
    } finally {
      setSwitchingTenantId(null);
    }
  };

  return (
    <div className={`min-h-screen font-sans ${isSuperAdmin ? 'bg-slate-100' : 'bg-gray-50'}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden text-sans">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Compact Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 ${isSuperAdmin ? 'w-72 bg-gradient-to-b from-slate-950 via-slate-900 to-red-950' : 'w-64 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900'} text-white shadow-2xl transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-out lg:translate-x-0 flex flex-col border-r border-slate-700/50`}>
        {/* Logo Area */}
        <div className={`${isSuperAdmin ? 'h-24' : 'h-16'} flex items-center px-6 border-b border-slate-700/50 bg-slate-950/70 backdrop-blur-sm`}>
          <div className="flex items-center space-x-3 px-2 w-full">
            <img
              src="/images/aaywa-logo.png"
              alt="eSanza Logo"
              className={`${isSuperAdmin ? 'h-11 w-11 rounded-xl' : 'h-9 w-9 rounded-lg'} object-contain bg-white/95 p-1 shadow-sm`}
            />
            <div className="min-w-0">
              <span className="block text-lg font-bold tracking-tight text-white">{isSuperAdmin ? 'Platform Console' : 'eSanza'}</span>
              <span className={`mt-0.5 block text-[11px] font-black uppercase tracking-[0.18em] ${isSuperAdmin ? 'text-red-300' : 'text-brand-blue-300'}`}>
                {isSuperAdmin ? 'Platform Owner' : 'Platform'}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto custom-scrollbar">
          {isSuperAdmin && (
            <div className="mx-2 mb-5 rounded-2xl border border-red-400/20 bg-white/5 p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-red-500/20 p-2 text-red-200">
                  <BuildingOffice2Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-black text-white">Owner Workspace</p>
                  <p className="text-xs text-slate-400">Organisations, modules, security</p>
                </div>
              </div>
            </div>
          )}

          {!isSuperAdmin && user?.tenant_id && (
            <div className="mx-2 mb-5">
              <button
                type="button"
                onClick={() => setShowWorkspaceSwitcher((value) => !value)}
                className="w-full rounded-2xl border border-brand-blue-400/20 bg-white/5 p-4 text-left shadow-inner shadow-white/5 transition hover:border-brand-blue-300/40 hover:bg-white/10"
              >
                <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-blue-500/15 text-brand-blue-200 ring-1 ring-brand-blue-300/20">
                  <BuildingOffice2Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Workspace</p>
                  <p className="mt-1 truncate text-sm font-black text-white" title={tenantName}>
                    {tenantName}
                  </p>
                  {(tenantCountry || tenantCurrency) && (
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {[tenantCountry, tenantCurrency].filter(Boolean).join(' • ')}
                    </p>
                  )}
                  {tenantDomain && (
                    <p className="mt-1 truncate text-[11px] font-semibold text-brand-blue-200" title={tenantDomain}>
                      {tenantDomain}
                    </p>
                  )}
                </div>
                  <ChevronDownIcon className={`mt-1 h-4 w-4 shrink-0 text-slate-500 transition ${showWorkspaceSwitcher ? 'rotate-180 text-brand-blue-200' : ''}`} />
                </div>
                <p className="mt-3 text-[11px] font-semibold text-slate-500">
                  {hasMultipleWorkspaces ? 'Click to switch organisation' : 'Current organisation workspace'}
                </p>
              </button>

              {showWorkspaceSwitcher && (
                <div className="mt-2 rounded-2xl border border-slate-700 bg-slate-900/95 p-2 shadow-xl">
                  <div className="mb-2 flex items-center justify-between px-2 pt-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Available workspaces</p>
                    {membershipsLoading && <span className="text-[10px] font-bold text-slate-400">Loading</span>}
                  </div>
                  <div className="space-y-1.5">
                    {memberships.map((membership) => {
                      const active = Number(membership.tenant_id) === Number(user?.tenant_id);
                      return (
                        <button
                          key={membership.tenant_id}
                          type="button"
                          disabled={active || switchingTenantId === membership.tenant_id}
                          onClick={() => handleSwitchWorkspace(membership.tenant_id)}
                          className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition ${
                            active
                              ? 'border-emerald-400/40 bg-emerald-500/10 text-white'
                              : 'border-slate-700 bg-white/[0.03] text-slate-300 hover:border-brand-blue-400/40 hover:bg-white/10 hover:text-white'
                          } ${switchingTenantId === membership.tenant_id ? 'opacity-60' : ''}`}
                        >
                          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-black ${
                            active ? 'bg-emerald-400 text-emerald-950' : 'bg-slate-700 text-slate-200'
                          }`}>
                            {(membership.tenant_name || 'O').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-black">{membership.tenant_name || 'Organisation'}</p>
                            <p className="truncate text-[10px] font-semibold text-slate-400 capitalize">
                              {String(membership.role || 'member').replace('_', ' ')}
                              {membership.country ? ` • ${membership.country}` : ''}
                            </p>
                          </div>
                          {active && <span className="rounded-full bg-emerald-400/15 px-2 py-1 text-[10px] font-black text-emerald-200">Current</span>}
                          {switchingTenantId === membership.tenant_id && <span className="text-[10px] font-bold text-brand-blue-200">Switching</span>}
                        </button>
                      );
                    })}
                    {!membershipsLoading && memberships.length <= 1 && (
                      <div className="rounded-xl border border-slate-700 bg-white/[0.03] px-3 py-2 text-[11px] text-slate-400">
                        {memberships.length === 1 ? 'Only one organisation is linked to this account.' : 'No linked organisations found.'}
                      </div>
                    )}
                    {workspaceError && (
                      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-[11px] font-semibold text-red-200">
                        {workspaceError}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {noModulesEnabled && (
            <div className="mx-2 mb-4 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300">
                No modules enabled
              </p>
              <p className="mt-2 text-xs font-semibold leading-relaxed text-amber-100">
                No operational modules are enabled for this workspace yet. Your account, profile
                and workspace switching still work. Ask the platform owner to enable a module.
              </p>
            </div>
          )}

          <div className="px-3 mb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            {isSuperAdmin ? 'Platform Control' : 'Navigation'}
          </div>
          {(() => {
            const items = isSuperAdmin ? superAdminPrimary : normalPrimary;
            const groups: Record<string, any[]> = {};
            items.forEach(item => {
              const g = item.group || 'Other';
              if (!groups[g]) groups[g] = [];
              groups[g].push(item);
            });
            const order = ['_top', 'Farm Operations', 'Supply Chain', 'Finance', 'Marketplace', 'Operations', 'Learning', 'Support', '_admin', 'Governance', '_superadmin'];
            return order.filter(g => groups[g]).map(g => (
              <div key={g}>
                {g !== '_top' && g !== '_superadmin' && (
                  <div className="px-3 pt-4 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-[0.16em]">
                    {g}
                  </div>
                )}
                {groups[g].map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href + item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                        ? isSuperAdmin
                          ? 'bg-red-500/15 text-red-200 shadow-sm border border-red-400/30'
                          : 'bg-brand-blue-600/15 text-brand-blue-300 shadow-sm border border-brand-blue-600/30'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                        }`}
                    >
                      <Icon className={`w-5 h-5 mr-3 transition-colors ${isActive ? (isSuperAdmin ? 'text-red-200' : 'text-brand-blue-300') : 'text-slate-400 group-hover:text-white'}`} />
                      <span className="truncate">{item.name}</span>
                      {isActive && (
                        <div className={`ml-auto w-1.5 h-1.5 rounded-full ${isSuperAdmin ? 'bg-red-300' : 'bg-brand-blue-400 shadow-[0_0_8px_rgba(56,197,255,0.6)]'}`} />
                      )}
                    </Link>
                  );
                })}
              </div>
            ));
          })()}

          {isSuperAdmin && (
            <div className="mx-2 mt-5 rounded-2xl border border-slate-700/60 bg-slate-950/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Operational access</p>
              <p className="mt-2 text-sm text-slate-300">
                Daily farming pages are tenant workspaces. Use Manage Organisations to enable or hide them per organisation.
              </p>
              <Link
                to="/dashboard/company-management"
                onClick={() => setSidebarOpen(false)}
                className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-white/10 px-3 py-2 text-xs font-black text-white hover:bg-white/15"
              >
                Manage Organisations
              </Link>
            </div>
          )}

          {!isSuperAdmin && user && user.role === 'project_manager' && normalGovernance.length > 0 && (
            <>
              <div className="px-3 pt-4 pb-1 mt-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-t border-slate-700/50">
                Governance
              </div>
              {normalGovernance.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-red-600/15 text-red-300 shadow-sm border border-red-600/30'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                    }`}>
                    <Icon className={`w-5 h-5 mr-3 transition-colors ${isActive ? 'text-red-300' : 'text-slate-400 group-hover:text-white'}`} />
                    <span className="truncate">{item.name}</span>
                    {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-red-400" />}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Profile Footer */}
        <div className="p-3 border-t border-slate-700/50 bg-slate-800/50">
          <div className="relative">
            <div
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer group"
            >
              <div className="w-8 h-8 rounded-full bg-slate-600 border border-slate-500 flex items-center justify-center text-xs font-bold text-slate-200 group-hover:border-slate-400">
                {user?.full_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.full_name || 'User'}</p>
                <p className="text-xs text-slate-400 truncate capitalize">
                  {hasMultipleWorkspaces ? `${memberships.length} organisations` : (user?.role?.replace('_', ' ') || 'Role')}
                </p>
              </div>
              <ChevronDownIcon className="w-3 h-3 text-slate-500 group-hover:text-slate-300" />
            </div>
            <p className="mt-2 text-[10px] text-slate-500 text-center">
              v{VERSION_INFO.backend_version || VERSION_INFO.frontend_version || '1.0.0'}
              {VERSION_INFO.git_commit && VERSION_INFO.git_commit !== 'unknown' ? ` • ${VERSION_INFO.git_commit}` : ''}
            </p>

            {/* User dropdown menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-3 right-3 mb-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-800 shadow-2xl">
                {!isSuperAdmin && (
                  <div className="border-b border-slate-700/80 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Organisations</p>
                      {membershipsLoading && <span className="text-[10px] font-bold text-slate-400">Loading</span>}
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {memberships.map((membership) => {
                        const active = Number(membership.tenant_id) === Number(user?.tenant_id);
                        return (
                          <button
                            key={membership.tenant_id}
                            type="button"
                            disabled={active || switchingTenantId === membership.tenant_id}
                            onClick={() => handleSwitchWorkspace(membership.tenant_id)}
                            className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left transition ${
                              active
                                ? 'border-emerald-400/40 bg-emerald-500/10 text-white'
                                : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-brand-blue-400/40 hover:bg-slate-700/60 hover:text-white'
                            } ${switchingTenantId === membership.tenant_id ? 'opacity-60' : ''}`}
                          >
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-black ${
                              active ? 'bg-emerald-400 text-emerald-950' : 'bg-slate-700 text-slate-200'
                            }`}>
                              {(membership.tenant_name || 'O').charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-black">{membership.tenant_name || 'Organisation'}</p>
                              <p className="truncate text-[10px] font-semibold text-slate-400 capitalize">
                                {String(membership.role || 'member').replace('_', ' ')}
                                {membership.country ? ` • ${membership.country}` : ''}
                              </p>
                            </div>
                            {active && <span className="rounded-full bg-emerald-400/15 px-2 py-1 text-[10px] font-black text-emerald-200">Current</span>}
                            {switchingTenantId === membership.tenant_id && <span className="text-[10px] font-bold text-brand-blue-200">Switching</span>}
                          </button>
                        );
                      })}
                      {!membershipsLoading && memberships.length <= 1 && (
                        <div className="rounded-xl border border-slate-700 bg-slate-900/40 px-3 py-2 text-[11px] text-slate-400">
                          {memberships.length === 1 ? 'Only one organisation is linked to this account.' : 'No linked organisations found.'}
                        </div>
                      )}
                      {workspaceError && (
                        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-[11px] font-semibold text-red-200">
                          {workspaceError}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <Link
                  to="/dashboard/profile"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                >
                  <UserCircleIcon className="w-4 h-4 mr-2" />
                  Profile
                </Link>
                <Link
                  to="/dashboard/settings"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                >
                  <CogIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
                <div className="h-px bg-slate-700 my-1" />
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`${isSuperAdmin ? 'lg:pl-80' : 'lg:pl-72'} flex flex-col min-h-screen transition-all duration-300`}>
        {/* Top Header */}
        <header className={`sticky top-0 z-20 ${isSuperAdmin ? 'bg-slate-950/95 text-white border-slate-800' : 'bg-white/80 border-gray-200/50'} backdrop-blur-md border-b px-4 py-4 lg:px-8 flex items-center justify-between`}>
          <button
            onClick={() => setSidebarOpen(true)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${isSuperAdmin ? 'text-slate-200 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Search Bar */}
          <div className="hidden md:flex items-center flex-1 max-w-md ml-4">
            {isSuperAdmin ? (
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-red-300">Super Admin</p>
                <h1 className="text-lg font-black text-white">Platform Governance Console</h1>
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <GlobalSearch />
                {!isSuperAdmin && user?.tenant_id && (
                  <div className="hidden xl:flex max-w-xs items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                      <BuildingOffice2Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Current workspace</p>
                      <p className="truncate text-sm font-black text-slate-800" title={tenantName}>{tenantName}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3 lg:space-x-6 ml-auto">
            <LanguageSwitcher />
            <NotificationCenter />
            <div className={`h-6 w-px ${isSuperAdmin ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
            <button className={`text-sm font-medium transition-colors ${isSuperAdmin ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              Help & Support
            </button>
          </div>
        </header>

        {/* Page Content Wrapper */}
        <main className={`flex-1 p-4 lg:p-8 overflow-x-hidden ${isSuperAdmin ? 'bg-slate-100' : ''}`}>
          <div className={`${isSuperAdmin ? 'max-w-[1500px]' : 'max-w-7xl'} mx-auto animate-fade-in`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
